/**
 * Question Card Component
 *
 * Displays a single question in the form builder list.
 * Shows question text, type icon, and quick actions.
 * Supports drag-and-drop reordering.
 */

import { forwardRef, useState } from 'react';
import {
  GripVertical,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  GitBranch,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Smartphone,
  Monitor,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { InlineTextEditor } from '@/components/ui/inline-text-editor';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { getQuestionTypeInfo } from '@/lib/forms/question-types';
import { FormQuestion, DeviceMode } from './hooks/useFormBuilder';

interface QuestionCardProps {
  question: FormQuestion;
  index: number;
  isSelected: boolean;
  isExpanded: boolean;
  deviceMode: DeviceMode;
  isVisibleOnDevice: boolean;
  onSelect: () => void;
  onToggleExpand: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleVisibility: (device?: DeviceMode) => void;
  onInlineEdit?: (field: 'text', value: string) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  isDragging?: boolean;
  className?: string;
  children?: React.ReactNode; // For expanded editor content
}

export const QuestionCard = forwardRef<HTMLDivElement, QuestionCardProps>(
  function QuestionCard(
    {
      question,
      index,
      isSelected,
      isExpanded,
      deviceMode,
      isVisibleOnDevice,
      onSelect,
      onToggleExpand,
      onDelete,
      onDuplicate,
      onToggleVisibility,
      onInlineEdit,
      dragHandleProps,
      isDragging,
      className,
      children,
    },
    ref
  ) {
    const typeInfo = getQuestionTypeInfo(question.type);
    const Icon = typeInfo?.icon;
    const hasBranching = question.branchingRules && question.branchingRules.length > 0;

    return (
      <div
        ref={ref}
        className={cn(
          'group rounded-lg border bg-card transition-all',
          isSelected && 'ring-2 ring-primary border-primary',
          isDragging && 'shadow-lg opacity-90',
          !isVisibleOnDevice && 'opacity-50',
          className
        )}
      >
        {/* Card Header */}
        <div
          className={cn(
            'flex items-center gap-2 p-3 cursor-pointer',
            isExpanded && 'border-b'
          )}
          onClick={onSelect}
        >
          {/* Drag Handle */}
          <div
            {...dragHandleProps}
            className={cn(
              'flex-shrink-0 cursor-grab active:cursor-grabbing',
              'text-muted-foreground hover:text-foreground transition-colors'
            )}
            onClick={e => e.stopPropagation()}
          >
            <GripVertical className="h-5 w-5" />
          </div>

          {/* Question Number */}
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center">
            <span className="text-xs font-medium">{index + 1}</span>
          </div>

          {/* Type Icon */}
          {Icon && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-shrink-0 text-muted-foreground">
                  <Icon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                {typeInfo?.label}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Question Text */}
          <div
            className="flex-1 min-w-0"
            onClick={e => e.stopPropagation()}
          >
            {onInlineEdit ? (
              <InlineTextEditor
                value={question.text || ''}
                onSave={(value) => onInlineEdit('text', value)}
                placeholder="Untitled Question"
                variant="light"
                className="text-sm font-medium"
              />
            ) : (
              <p className="text-sm font-medium truncate">
                {question.text || 'Untitled Question'}
              </p>
            )}
          </div>

          {/* Status Badges */}
          <div className="flex items-center gap-1">
            {question.required && (
              <Badge variant="secondary" className="text-xs">
                Required
              </Badge>
            )}
            {hasBranching && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-1 text-blue-500">
                    <GitBranch className="h-4 w-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>Has branching logic</TooltipContent>
              </Tooltip>
            )}
            {!isVisibleOnDevice && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-1 text-muted-foreground">
                    <EyeOff className="h-4 w-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  Hidden on {deviceMode === 'mobile' ? 'mobile' : 'desktop'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Expand/Collapse Button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={e => {
              e.stopPropagation();
              onToggleExpand();
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>

          {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity',
                  isSelected && 'opacity-100'
                )}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onDuplicate}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleVisibility()}>
                {isVisibleOnDevice ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide on {deviceMode}
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show on {deviceMode}
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onToggleVisibility('mobile')}>
                <Smartphone className="h-4 w-4 mr-2" />
                Toggle mobile visibility
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleVisibility('desktop')}>
                <Monitor className="h-4 w-4 mr-2" />
                Toggle desktop visibility
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Expanded Editor Content */}
        {isExpanded && children && (
          <div className="p-4" onClick={e => e.stopPropagation()}>
            {children}
          </div>
        )}
      </div>
    );
  }
);
